import React, { useState } from "react";
import { Button, Dialog, Divider, Grid, TextField, Typography } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";

import FeedbackSelection from "./FeedbackSelection";
import { Link } from "react-router-dom";
import {
  FeedbackQuestionDto,
  WorkshopInstanceDto,
  WorkshopInstanceUserFeedbackDto,
} from "../../../types/workshopTypes";

interface WorkshopInstanceFeedbackFormProps {
  onSumbit: () => Promise<void>;
  workshopInstance: WorkshopInstanceDto;
  feedbackQuestions: FeedbackQuestionDto[];
  workshopType: string;
  userFeedback: WorkshopInstanceUserFeedbackDto;
  updateFeedbackGrade: (feedbackQuestionId: number, grade: number) => void;
  updateReferentFeedbackGrade: (memberId: number, grade: number) => void;
  setUserFeedback: React.Dispatch<React.SetStateAction<WorkshopInstanceUserFeedbackDto>>;
  workshopInstanceTextQuestions: { [key: string]: string };
}

/**
 * Displays the feedback questions form for a workshop instance
 * @param onSumbit The function to call when the form is submitted
 * @param workshopInstance The workshop instance to display the feedback form for
 * @returns The WorkshopInstanceFeedbackForm component
 */
const WorkshopInstanceFeedbackForm: React.FunctionComponent<WorkshopInstanceFeedbackFormProps> = ({
  onSumbit,
  workshopInstance,
  feedbackQuestions,
  workshopType,
  userFeedback,
  updateFeedbackGrade,
  updateReferentFeedbackGrade,
  setUserFeedback,
  workshopInstanceTextQuestions,
}: WorkshopInstanceFeedbackFormProps) => {
  const isMobile = useResponsive("down", "sm");

  const [checkAnswered, setCheckAnswered] = useState(false);
  const [open, setOpen] = useState(false);

  // Function to check if all questions have a selected value
  const allQuestionsAnswered = () => {
    const feedbackQuestionsAnswered = feedbackQuestions?.every(
      (question) =>
        userFeedback?.feedback.find((feedback) => feedback.feedbackQuestionId === question.feedbackQuestionId)
          ?.grade !== undefined
    );

    const referentsFeedbackAnswered = workshopInstance?.referents.every(
      (referent) =>
        userFeedback?.referentsFeedback.find((feedback) => feedback.memberId === referent.memberId)?.grade !== undefined
    );

    if (workshopType === "Externer Workshop") {
      return feedbackQuestionsAnswered;
    }
    return feedbackQuestionsAnswered && referentsFeedbackAnswered;
  };

  // Function to check if a specific question has a selected value
  const questionAnswered = (questionID: number) => {
    const answer = userFeedback?.feedback.find((feedback) => feedback.feedbackQuestionId === questionID)?.grade;
    return answer !== undefined;
  };

  // Function to check if a specific referent has a selected value
  const referentGraded = (memberId: number) => {
    const answer = userFeedback?.referentsFeedback.find((feedback) => feedback.memberId === memberId)?.grade;
    return answer !== undefined;
  };

  // Function to handle the submit button and save the feedback
  const handleSubmit = () => {
    setCheckAnswered(true);
    if (!allQuestionsAnswered()) {
      setOpen(true);
      return;
    }
    onSumbit();
  };

  return (
    <>
      {/* Display the numberic feedback questions */}
      <Grid container spacing={1}>
        {feedbackQuestions.map((question) => (
          <Grid key={question.feedbackQuestionId} container item xs={12}>
            <Grid item xs={12} sm={9.7}>
              <Typography>{question.question}</Typography>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ mt: isMobile ? 1 : 0 }}>
              <FeedbackSelection
                questionId={question.feedbackQuestionId}
                feedbackValue={
                  userFeedback?.feedback.find((f) => f.feedbackQuestionId === question.feedbackQuestionId)?.grade
                }
                handleFeedbackChange={updateFeedbackGrade}
                questionAnswered={questionAnswered}
                checkAnswered={checkAnswered}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Divider />
            </Grid>
          </Grid>
        ))}

        {/* Display the referents feedback questions */}
        {workshopType !== "Externer Workshop" && workshopInstance.referents.length > 0
          ? workshopInstance.referents.map((referent) => (
              <Grid key={referent.memberId} container item xs={12}>
                <Grid item xs={12} sm={9.7}>
                  <Typography>
                    Wie bewertest Du die Präsentationsfähigkeiten der/des Referentin/Referenten {referent.firstname}{" "}
                    {referent.lastname}?
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ mt: isMobile ? 1 : 0 }}>
                  <FeedbackSelection
                    questionId={referent.memberId} // Using memberId as questionId for referent feedback
                    feedbackValue={userFeedback?.referentsFeedback.find((f) => f.memberId === referent.memberId)?.grade}
                    handleFeedbackChange={updateReferentFeedbackGrade}
                    questionAnswered={referentGraded}
                    checkAnswered={checkAnswered}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Divider />
                </Grid>
              </Grid>
            ))
          : null}

        {/* Display the text feedback questions */}
        <Grid container item xs={12} spacing={2} alignItems={"start"} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography>{workshopInstanceTextQuestions["workshopLevel"]}</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Antwort"
              rows={4}
              InputLabelProps={{ shrink: true }}
              value={userFeedback.workshopLevel || ""}
              onChange={(event) =>
                setUserFeedback({
                  ...userFeedback,
                  workshopLevel: event.target.value,
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems={"start"} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography>{workshopInstanceTextQuestions["contentFeedback"]}</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Antwort"
              rows={4}
              InputLabelProps={{ shrink: true }}
              value={userFeedback.contentFeedback || ""}
              onChange={(event) =>
                setUserFeedback({
                  ...userFeedback,
                  contentFeedback: event.target.value,
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems={"start"} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography>{workshopInstanceTextQuestions["presentationFeedback"]}</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Antwort"
              rows={4}
              InputLabelProps={{ shrink: true }}
              value={userFeedback.presentationFeedback || ""}
              onChange={(event) =>
                setUserFeedback({
                  ...userFeedback,
                  presentationFeedback: event.target.value,
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems={"start"} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography>{workshopInstanceTextQuestions["workshopNeed"]}</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Antwort"
              rows={4}
              InputLabelProps={{ shrink: true }}
              value={userFeedback.workshopNeed || ""}
              onChange={(event) =>
                setUserFeedback({
                  ...userFeedback,
                  workshopNeed: event.target.value,
                })
              }
            />
          </Grid>
        </Grid>

        <Grid item xs={6} sx={{ mt: 2 }}>
          <Button variant="contained" fullWidth component={Link} to={"/workshops"}>
            Abbrechen
          </Button>
        </Grid>
        <Grid item xs={6} sx={{ mt: 2 }}>
          <Button variant="contained" fullWidth onClick={handleSubmit}>
            Absenden
          </Button>
        </Grid>
      </Grid>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <Typography sx={{ p: 2 }}>Bitte beantworte alle Fragen</Typography>
        <Button variant="contained" onClick={() => setOpen(false)} sx={{ mb: 2, ml: 2, mr: 2 }}>
          OK
        </Button>
      </Dialog>
    </>
  );
};

export default WorkshopInstanceFeedbackForm;
